var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createSelector } from "@reduxjs/toolkit";
import { bankIDApi } from "apis/eduidBankid";
import { eidasApi } from "apis/eduidEidas";
import { frejaeIDApi } from "apis/eduidFrejaeID";
import { securityApi } from "apis/eduidSecurity";
import { navigatorCredentialsApi } from "apis/navigatorCredentials";
import { EduIDButton } from "components/Common/EduIDButton";
import { ToolTip } from "components/Common/ToolTip";
import { SecurityKeyTable } from "components/Dashboard/SecurityKeyTable";
import { useAppDispatch, useAppSelector } from "eduid-hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router";
import authnSlice from "slices/Authn";
import passKey from "../../../img/pass-key.svg";
import securityKey from "../../../img/security-key.svg";
import { useReAuthenticate } from "../Hooks/useReAuthenticate";
import { VerifyCredentialModal } from "./VerifyCredentialModal";
import { WebauthnDescriptionModal } from "./WebauthnDescriptionModal";
export const FRONTEND_ACTION = "frontend_action";
const selectCredentials = (state) => state.security.credentials;
export const filterTokensFromCredentials = createSelector([selectCredentials], (credentials) => credentials.filter((cred) => cred.credential_type === "security.u2f_credential_type" ||
    cred.credential_type === "security.webauthn_credential_type"));
export function MultiFactorAuthentication() {
    var _a, _b, _c, _d;
    const return_handled = useRef(false);
    const dispatch = useAppDispatch();
    const credentials = useAppSelector((state) => state.security.credentials);
    const [isPlatformAuthenticatorAvailable, setIsPlatformAuthenticatorAvailable] = useState(false);
    // Start as loaded (true) if WebAuthn API doesn't exist (nothing async to wait for)
    const [isPlatformAuthLoaded, setIsPlatformAuthLoaded] = useState(() => !globalThis.PublicKeyCredential);
    const [showSecurityKeyNameModal, setShowSecurityKeyNameModal] = useState(false);
    const [showVerifyWebauthnModal, setShowVerifyWebauthnModal] = useState(false);
    const isLoaded = useAppSelector((state) => state.config.is_app_loaded);
    const wrapperRef = useRef(null);
    const identities = useAppSelector((state) => { var _a; return (_a = state.personal_data.response) === null || _a === void 0 ? void 0 : _a.identities; });
    const [requestCredentials] = securityApi.useLazyRequestCredentialsQuery();
    const [beginRegisterWebauthn] = securityApi.useLazyBeginRegisterWebauthnQuery();
    const [registerWebauthn] = securityApi.useLazyRegisterWebauthnQuery();
    const [bankIDVerifyCredential] = bankIDApi.useLazyBankIDVerifyCredentialQuery();
    const [eidasVerifyCredential] = eidasApi.useLazyEidasVerifyCredentialQuery();
    const [createCredential] = navigatorCredentialsApi.useLazyCreateCredentialQuery();
    const [frejaeidVerifyCredential] = frejaeIDApi.useLazyFrejaeIDVerifyCredentialQuery();
    const [removeWebauthnToken] = securityApi.useLazyRemoveWebauthnTokenQuery();
    const { checkAuthnStatus } = useReAuthenticate();
    const tokens = useAppSelector(filterTokensFromCredentials);
    // Derive tokenKey from the last token in the array
    const tokenKey = (_b = (_a = tokens.at(-1)) === null || _a === void 0 ? void 0 : _a.key) !== null && _b !== void 0 ? _b : "";
    const authn = useAppSelector((state) => state.authn);
    const [isRegisteringAuthenticator, setIsRegisteringAuthenticator] = useState(false);
    const tokenTypeMap = useMemo(() => ({
        freja: eidasVerifyCredential,
        bankid: bankIDVerifyCredential,
        eidas: eidasVerifyCredential,
        freja_eid: frejaeidVerifyCredential,
    }), [eidasVerifyCredential, bankIDVerifyCredential, frejaeidVerifyCredential]);
    const handleVerificationWebauthnToken = useCallback((token, method) => __awaiter(this, void 0, void 0, function* () {
        if (!token) {
            return;
        }
        const verifyAction = tokenTypeMap[method];
        const response = yield verifyAction({
            credential_id: token,
            method,
        });
        if (response.isSuccess) {
            if (response.data.payload.location) {
                globalThis.location.assign(response.data.payload.location);
            }
        }
        else if (response.isError) {
            setShowVerifyWebauthnModal(false);
            dispatch(authnSlice.actions.setFrontendActionAndState({
                frontend_action: "verifyCredential",
                frontend_state: JSON.stringify({
                    method,
                    credential: token,
                    description: response.error.payload.credential_description,
                }),
            }));
        }
    }), [tokenTypeMap, dispatch]);
    const handleRemoveWebauthnToken = useCallback((credential_string) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        let credential_key;
        try {
            credential_key = credential_string ? JSON.parse(credential_string).credential : undefined;
        }
        catch (_b) {
            return;
        }
        if (!credential_key)
            return;
        const response = yield removeWebauthnToken({ credential_key });
        if (response.isError) {
            // prepare authenticate() and AuthenticateModal
            dispatch(authnSlice.actions.setFrontendActionAndState({
                frontend_action: "removeSecurityKeyAuthn",
                frontend_state: credential_key,
            }));
        }
        else {
            (_a = wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }), [removeWebauthnToken, dispatch, wrapperRef]);
    const handleStopAskingWebauthnDescription = useCallback(() => {
        setIsRegisteringAuthenticator(false);
        setShowSecurityKeyNameModal(false);
    }, []);
    // buttons "My Device" or "My security key"
    const handleRegisterWebauthn = useCallback((authType) => __awaiter(this, void 0, void 0, function* () {
        setIsRegisteringAuthenticator(true);
        const isAuthed = yield checkAuthnStatus("addSecurityKeyAuthn", authType);
        if (isAuthed) {
            setShowSecurityKeyNameModal(true);
        }
        else {
            setIsRegisteringAuthenticator(false);
        }
    }), [checkAuthnStatus]);
    // function that is called when the user clicks OK in the "security key name" modal
    const handleStartWebauthnRegistration = useCallback((values) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const frontend_state = authn.frontend_state || ((_a = authn === null || authn === void 0 ? void 0 : authn.response) === null || _a === void 0 ? void 0 : _a.frontend_state);
        if (frontend_state) {
            const description_value = values["describe-webauthn-token-modal"];
            const description = description_value === null || description_value === void 0 ? void 0 : description_value.trim();
            setShowSecurityKeyNameModal(false);
            const registration = yield beginRegisterWebauthn({ authenticator: frontend_state });
            if (registration.isSuccess) {
                const createResponse = yield createCredential(registration.data.payload.registration_data.publicKey);
                if (createResponse.isSuccess) {
                    const registerResponse = yield registerWebauthn({
                        webauthn_attestation: createResponse.data,
                        description,
                    });
                    (_b = wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                    if (registerResponse.isSuccess) {
                        setShowVerifyWebauthnModal(true);
                    }
                }
            }
            dispatch(authnSlice.actions.setAuthnFrontendReset());
            setIsRegisteringAuthenticator(false);
        }
    }), [authn, beginRegisterWebauthn, createCredential, registerWebauthn, wrapperRef, dispatch]);
    // Runs after re-auth security zone
    useEffect(() => {
        if (return_handled.current) {
            return;
        }
        return_handled.current = true;
        function handleAuthReturn() {
            return __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                if (((_a = authn === null || authn === void 0 ? void 0 : authn.response) === null || _a === void 0 ? void 0 : _a.frontend_action) === "addSecurityKeyAuthn" && ((_b = authn === null || authn === void 0 ? void 0 : authn.response) === null || _b === void 0 ? void 0 : _b.frontend_state)) {
                    setShowSecurityKeyNameModal(true);
                }
                else if (((_c = authn === null || authn === void 0 ? void 0 : authn.response) === null || _c === void 0 ? void 0 : _c.frontend_action) === "removeSecurityKeyAuthn" && authn.response.frontend_state) {
                    yield handleRemoveWebauthnToken(authn.response.frontend_state);
                    dispatch(authnSlice.actions.setAuthnFrontendReset());
                }
                else if (((_d = authn === null || authn === void 0 ? void 0 : authn.response) === null || _d === void 0 ? void 0 : _d.frontend_action) === "verifyCredential" && authn.response.frontend_state) {
                    try {
                        const parsedFrontendState = JSON.parse(authn.response.frontend_state);
                        yield handleVerificationWebauthnToken(parsedFrontendState.credential, parsedFrontendState.method);
                    }
                    catch (_e) {
                        // Invalid JSON — skip
                    }
                }
            });
        }
        void handleAuthReturn();
    }, [
        (_c = authn.response) === null || _c === void 0 ? void 0 : _c.frontend_action,
        (_d = authn.response) === null || _d === void 0 ? void 0 : _d.frontend_state,
        dispatch,
        handleRemoveWebauthnToken,
        handleVerificationWebauthnToken,
    ]);
    useEffect(() => {
        (() => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (isLoaded && !credentials.length) {
                // call requestCredentials once app is loaded
                const response = yield requestCredentials();
                if (response.isSuccess) {
                    (_a = wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }
            }
        }))();
    }, [credentials.length, isLoaded, requestCredentials]);
    useEffect(() => {
        // Check if platform authentication is available through the navigator.credentials API.
        // Only runs if the API exists (otherwise isPlatformAuthLoaded starts as true)
        if (!globalThis.PublicKeyCredential) {
            return; // Nothing to do, already initialized as loaded
        }
        let aborted = false; // flag to avoid updating unmounted components after this promise resolves
        let platform = false;
        globalThis.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
            .then((available) => {
            platform = available;
        })
            .catch(() => { })
            .finally(() => {
            if (!aborted) {
                setIsPlatformAuthenticatorAvailable(platform);
                // don´t show content until isPlatformAuthLoaded updates to true
                setIsPlatformAuthLoaded(true);
            }
        });
        // create a cleanup function that will allow the async code above to realise it shouldn't
        // try to update state on an unmounted react component
        return () => {
            aborted = true;
        };
    }, []);
    if (!isPlatformAuthLoaded)
        return null;
    return (_jsxs(_Fragment, { children: [_jsxs("article", { id: "add-two-factor", children: [_jsxs("div", { className: "flex-between baseline", children: [_jsx("h2", { children: _jsx(FormattedMessage, { id: "mfaInfo.title", description: "security key title", defaultMessage: "Add multi-factor Authentication (MFA)" }) }), _jsx(ToolTip, {})] }), _jsx("p", { children: _jsx(FormattedMessage, { id: "mfaInfo.securitySecond", description: "security second factor", defaultMessage: `If possible add a security key as a second factor of authentication, beyond username and password, 
              to prove you are the owner of your eduID. Examples are separate physical USB security keys that you can get, or built-in passkey features on your device, such as biometrics or pins.` }) }), _jsx("p", { className: "text-medium", children: _jsx("strong", { children: _jsx(FormattedMessage, { id: "mfaInfo.security", description: "security second factor individual", defaultMessage: `Note: added security keys are personal and not to be shared with others. This is to ensure that access to your account is limited solely to you, the account holder.` }) }) }), _jsx("p", { className: "help-text", children: _jsx(FormattedMessage, { id: "mfaInfo.info", description: "security second factor help info", defaultMessage: `You can read more about supported security keys in the Help section: {HelpSecurityKeys}.`, values: {
                                HelpSecurityKeys: (_jsx(Link, { className: "text-link", to: `../../../help#help-security-key-button`, target: "_blank", children: _jsx(FormattedMessage, { id: "mfaInfo.handle", description: "about security key - handle", defaultMessage: "Improving the security level of eduID" }) })),
                            } }) }), _jsxs("section", { className: "add-key-section", children: [_jsx("span", { "aria-label": "select extra webauthn", children: _jsx("strong", { children: _jsx(FormattedMessage, { id: "mfaInfo.select", description: "select extra webauthn", defaultMessage: "Add a new security key:" }) }) }), _jsxs("div", { className: "buttons", children: [_jsxs("div", { children: [_jsxs(EduIDButton, { id: "security-webauthn-platform-button", buttonstyle: "primary icon", onClick: () => handleRegisterWebauthn("platform"), disabled: !isPlatformAuthenticatorAvailable || isRegisteringAuthenticator, children: [_jsx("img", { className: "pass-key-icon", height: "25", alt: "pass key icon", src: passKey }), _jsx(FormattedMessage, { id: "credentials.addWebauthn", description: "add webauthn token device", defaultMessage: "this device" })] }), _jsx("p", { className: "help-text", children: _jsx(FormattedMessage, { id: "credentials.platformAuthn", description: "platform authn device help text", defaultMessage: "Internal passkey on your phone or laptop." }) }), !isPlatformAuthenticatorAvailable && (_jsx("p", { className: "help-text black", children: _jsx(FormattedMessage, { id: "mfaInfo.error", description: "platform authn device error text", defaultMessage: "*Your device is not compatible." }) }))] }), _jsxs("div", { children: [_jsxs(EduIDButton, { id: "security-webauthn-button", buttonstyle: "primary icon", onClick: () => handleRegisterWebauthn("cross-platform"), disabled: isRegisteringAuthenticator, children: [_jsx("img", { className: "security-key-icon", height: "25", alt: "", src: securityKey }), _jsx(FormattedMessage, { id: "common.securityKeyText", description: "add webauthn token key", defaultMessage: "security key" })] }), _jsx("p", { className: "help-text", children: _jsx(FormattedMessage, { id: "credentials.platform", description: "platform authn key help text", defaultMessage: "Your external USB security key." }) })] })] }), tokens.length === 1 && !((identities === null || identities === void 0 ? void 0 : identities.nin) && (identities === null || identities === void 0 ? void 0 : identities.is_verified)) && (_jsx("span", { className: "suggestion suggestion-txt", children: _jsx(FormattedMessage, { id: "multiFactorAuth.multiple", description: "multiple key suggestion", defaultMessage: "It is strongly recommended to {strong} security key or passkey to ensure you can still log in to your account if one is lost.", values: {
                                        strong: (_jsx("strong", { children: _jsx(FormattedMessage, { id: "multiFactorAuth.multipleKey", description: "multiple key - strong", defaultMessage: `add more than one` }) })),
                                    } }) }))] })] }), _jsx(SecurityKeyTable, { wrapperRef: wrapperRef, handleVerificationWebauthnToken: handleVerificationWebauthnToken, handleRemoveWebauthnToken: handleRemoveWebauthnToken }), _jsx(WebauthnDescriptionModal, { showModal: showSecurityKeyNameModal, closeModal: handleStopAskingWebauthnDescription, handleConfirm: handleStartWebauthnRegistration }), _jsx(VerifyCredentialModal, { showVerifyWebauthnModal: showVerifyWebauthnModal, setShowVerifyWebauthnModal: setShowVerifyWebauthnModal, handleVerificationWebauthnToken: handleVerificationWebauthnToken, tokenKey: tokenKey !== null && tokenKey !== void 0 ? tokenKey : "" })] }));
}
